import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

import { APP_ROUTES } from '../../app.constants';
import { ROLES_STATUS } from '../../enums/roles-status.enum';
import { AccountDTO } from '../../interfaces/models';
import { UserService } from '../../providers/user/user.service';
import { DirectivesModule } from '../../directives.module';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatListModule, DirectivesModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  user: AccountDTO;
  userSubscription: Subscription;

  public categories: Categories[] = [];

  private hasRole(roles: ROLES_STATUS[], user: AccountDTO = this.user): boolean {
    return !!user && roles.includes(user.roleId);
  }

  private buildCategories(user: AccountDTO): Categories[] {
    return [
      {
        name: 'Dashboard',
        routes: [{
          name: 'Dashboard', url: '/dashboard',
          icon: 'dashboard',
          isPrimary: true,
          visible: this.hasRole([
            ROLES_STATUS.SUPER_ADMIN,
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.SUPER_ADMIN,
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Orders',
        routes: [{
          name: 'Orders', url: '/orders/list',
          icon: 'insert_drive_file',
          isPrimary: true,
          notificationKey: 'notificationOrders',
          visible: this.hasRole([
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Orders',
        routes: [{
          name: 'Orders',
          url: '/sys-order/list',
          icon: 'insert_drive_file',
          isPrimary: true,
          notificationKey: 'notificationOrders',
          visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        }],
        visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        isOpen: false,
      },
      {
        name: 'Carriers',
        routes: [{
          name: 'Carriers', url: '/company/list',
          icon: 'business_outline',
          isPrimary: true,
          notificationKey: 'notificationCompanies',
          visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        }],
        visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        isOpen: false,
      },
      {
        name: 'My Team',
        routes: [
          { name: 'My Team', icon: 'account_box_outline', isPrimary: true, notificationKey: 'notificationUsers', visible: true },
          { name: 'Drivers', url: '/accounts/drivers', isPrimary: false, visible: true },
          { name: 'Dispatchers', url: '/accounts/dispatchers', isPrimary: false, visible: true },
          { name: 'Clients', url: '/accounts/clients', isPrimary: false, visible: !this.hasRole([ROLES_STATUS.COMPANY_ADMIN], user) },
        ].filter(route => route.visible),
        visible: this.hasRole([
          ROLES_STATUS.SUPER_ADMIN,
          ROLES_STATUS.COMPANY_ADMIN,
        ], user),
        isOpen: false,
      },
      {
        name: 'My Drivers',
        routes: [{
          name: 'My Drivers',
          url: '/accounts/dispatcher-role',
          icon: 'account_box_outline',
          isPrimary: true,
          notificationKey: 'notificationUsers',
          visible: this.hasRole([ROLES_STATUS.DISPATCHER], user),
        }],
        visible: this.hasRole([ROLES_STATUS.DISPATCHER], user),
        isOpen: false,
      },
      {
        name: 'Leads',
        routes: [{
          name: 'Leads',
          url: '/leeds/list',
          icon: 'insert_drive_file',
          isPrimary: true,
          notificationKey: 'notificationQuotes',
          visible: this.hasRole([
            ROLES_STATUS.SUPER_ADMIN,
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.SUPER_ADMIN,
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Load Board',
        routes: [{
          name: 'Load Board', url: '/order/list', icon: 'insert_drive_file', isPrimary: true,
          visible: this.hasRole([
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Trips',
        routes: [{
          name: 'Trips', url: '/trip/list', icon: 'library_books', isPrimary: true,
          visible: this.hasRole([
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Drivers location',
        routes: [{
          name: 'Drivers location', url: APP_ROUTES.drivers_location, icon: 'insert_drive_file', isPrimary: true,
          visible: this.hasRole([
            ROLES_STATUS.SUPER_ADMIN,
            ROLES_STATUS.COMPANY_ADMIN,
            ROLES_STATUS.DISPATCHER,
          ], user),
        }],
        visible: this.hasRole([
          ROLES_STATUS.SUPER_ADMIN,
          ROLES_STATUS.COMPANY_ADMIN,
          ROLES_STATUS.DISPATCHER,
        ], user),
        isOpen: false,
      },
      {
        name: 'Price policy',
        routes: [{
          name: 'Price policy', url: '/policy/list', icon: 'money', isPrimary: true,
          visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        }],
        visible: this.hasRole([ROLES_STATUS.SUPER_ADMIN], user),
        isOpen: false,
      },
      {
        name: 'Reports',
        routes: [
          { name: 'Reports', icon: 'business', isPrimary: true, visible: true },
          { name: 'Company Revenue', url: '/reports/revenue', isPrimary: false, visible: !this.hasRole([ROLES_STATUS.DISPATCHER], user) },
          { name: 'Reports by user', url: '/reports/user', isPrimary: false, visible: true },
        ].filter(route => route.visible),
        visible: this.hasRole([ROLES_STATUS.COMPANY_ADMIN, ROLES_STATUS.DISPATCHER], user),
        isOpen: false,
      },
    ].filter(category => category.visible);
  }

  public toggleChildren(category: Categories) {
    if (category.name === 'Reports' || category.name === 'My Team') {
      return this.categories = this.categories.map(
        element => element.name === category.name ? { ...element, isOpen: !element.isOpen } : element);
    }
  }

  constructor(public router: Router, private userService: UserService) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.userService.closeSidebar();
      }
    });
  }

  ngOnInit() {
    this.user = this.userService.user;
    this.categories = this.buildCategories(this.user);
    this.userSubscription = this.userService
      .updateUser
      .subscribe((value) => {
        this.user = value;
        this.categories = this.buildCategories(value);
      });
  }

  ngOnDestroy() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }
}

interface Categories {
  name: string;
  routes: Route[];
  visible?: boolean;
  subRoutes?: Route[];
  isOpen?: boolean;
}

interface Route {
  notificationKey?: string;
  name: string;
  url?: string;
  icon?: string;
  visible?: boolean;
  isPrimary: boolean;
}
